//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { IReduxState } from "@reducers/root.reducer";
// one-on-one-autopilot imports
import { IViewProps, IViewState, IEventApStatus, IProps } from "./OneOnOneAutopilot.d";
import { OneOnOneAutopilotColumnsView } from "./";
import { styles } from "./OneOnOnAutopilot.styles";
// util
import * as _ from "lodash";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { logger } from "@util/logger.util";
// other components
import { A, H1 } from "@ui";
import { time } from "@clockwise/web-commons/src/styles/color.styles";
import { articleUrls } from "@clockwise/client-commons/src/constants/help-center";
import { track, TrackingEvents } from "@util/analytics.util";
import { addAndRemoveOrgTags, batchModifyAutopilotForEvents } from "@mutations";
import { EFlex, generateEventFormSaveTags, Tags } from "@util/event-tag.util";
import { createEventParentRelayIdFromOrgRelayId } from "@util/event.util";
// material-ui
import withStyles from "@material-ui/core/styles/withStyles";
import { CheckCircle } from "@material-ui/icons";
import { RadioGroup, Radio, FormControlLabel } from "@material-ui/core";
import { ChangeEvent } from "react";
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";

const GOOD_NUM_AP_THRESHOLD = 0.7;

//////////////////
// COMPONENTS
//////////////////

const RightNumberAP = ({
  goodNumAP,
  onApCount,
  totalCount,
}: {
  goodNumAP: number;
  onApCount: number;
  totalCount: number;
}) => {
  const t = React.useContext(TranslationContext);
  return totalCount > 0 ? (
    <div>
      <H1>
        {onApCount >= goodNumAP ? t("1-on-1:progress-title-good") : t("1-on-1:progress-title-bad")}
      </H1>
      <div style={styles.subheader}>
        {onApCount >= goodNumAP
          ? t("1-on-1:progress-subtitle-good")
          : t("1-on-1:progress-subtitle-bad", { count: onApCount, desiredCount: goodNumAP })}
      </div>
    </div>
  ) : (
    <div>
      <H1>Looks like you don't have any recurring one-on-ones on your calendar</H1>
      <div style={styles.subheader}>
        Clockwise will display all of your recurring one-on-ones in this view.
      </div>
    </div>
  );
};

const _radioOptions = {
  ALL: "ALL",
  SOME: "SOME",
};

const ToggleAllOrSome = ({
  focusTimeMe,
  focusTimeOthers,
  value,
  onChange,
}: {
  focusTimeMe?: number;
  focusTimeOthers?: number;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>, value: string) => void;
}) => {
  const t = React.useContext(TranslationContext);
  return (
    <div style={styles.toggleRadios}>
      <RadioGroup value={value} onChange={onChange}>
        <FormControlLabel
          control={<Radio checkedIcon={<CheckCircle style={{ color: time.focus }} />} />}
          value={_radioOptions.ALL}
          label={
            <div>
              <div style={styles.radioMainText}>{t("1-on-1:mark-all")}</div>
              {focusTimeMe && (
                <div style={styles.radioSubtext}>
                  <span style={{ color: "green", fontWeight: "bold" }}>+{focusTimeMe}</span>
                  &nbsp;hrs of Focus Time for you
                </div>
              )}
              {focusTimeOthers && (
                <div style={styles.radioSubtext}>
                  <span style={{ color: "green", fontWeight: "bold" }}>+{focusTimeOthers}</span>
                  &nbsp;hrs of Focus Time for others
                </div>
              )}
            </div>
          }
          style={value === _radioOptions.ALL ? styles.toggleOptionSelected : styles.toggleOption}
        />
        <FormControlLabel
          control={<Radio checkedIcon={<CheckCircle style={{ color: time.focus }} />} />}
          value={_radioOptions.SOME}
          label={
            <div>
              <div style={styles.radioMainText}>Choose which one-on-ones</div>
              <div style={styles.radioSubtext}>{t("1-on-1:mark-prompt")}</div>
            </div>
          }
          style={value === _radioOptions.SOME ? styles.toggleOptionSelected : styles.toggleOption}
        />
      </RadioGroup>
    </div>
  );
};

const WhatHappensWhenAp = () => {
  const t = React.useContext(TranslationContext);
  return (
    <div className="cw-mt-6">
      ✨&nbsp;
      <A style={styles.whatHappens} href={articleUrls.autopilot} target="_blank">
        {t("1-on-1:autopilot-what-happens")}
      </A>
    </div>
  );
};

class OneOnOneAutopilotComponent extends React.Component<IProps, IViewState> {
  // ~-~-~-~-~-~-~-
  // Setup
  // ~-~-~-~-~-~-~-
  private opacityTimeout: ReturnType<typeof setTimeout>;

  constructor(props: IProps) {
    super(props);

    // create map of event externalEventId to an object describing whether AP is enabled for the event,
    // the event object, and the last know movementType/EFlex type from the server. With this, if a user
    // toggles AP off and then back on, we know to set it back to EFlex.Day or EFlex.Week.
    const eventsApEnabled = this.props.oneOnOnesForCalendar.reduce(
      (prev: IEventApStatus, curr: ISchema.IEvent) => {
        const eventOrgTags = curr.annotatedEvent.orgTags.filter(
          (orgTag) => orgTag.tag === Tags.MovementType,
        );
        const apEnabled =
          eventOrgTags.length === 1 ? eventOrgTags[0].state.value !== EFlex.Pin : false;
        const flexType = eventOrgTags.length === 1 ? eventOrgTags[0].state.value : undefined;
        prev[curr.annotatedEvent.externalEventId] = { enabled: apEnabled, event: curr, flexType };
        return prev;
      },
      {},
    );

    // Are all on AP?
    const allEnabled = _.every(
      eventsApEnabled,
      (e) => e.enabled || !this.props.canAutopilotMap[e.event.eventKey.externalEventId],
    );

    this.state = {
      eventsApEnabled: eventsApEnabled,
      prevEventsApEnabled: eventsApEnabled,
      toggleAllState: allEnabled,
      isOpaque: false,
    };
  }

  static contextType = TranslationContext;
  declare context: React.ContextType<typeof TranslationContext>;

  componentWillUnmount() {
    if (this.opacityTimeout !== undefined) {
      clearTimeout(this.opacityTimeout);
    }
  }

  // ~-~-~-~-~-~-~-
  // Saving
  // ~-~-~-~-~-~-~-

  private maybeGetPreviousMovementType(externalEventId: string) {
    const { eventsApEnabled } = this.state;

    switch (eventsApEnabled[externalEventId].flexType) {
      case EFlex.Day:
        return ISchema.ModifyAutopilotForEventMovementTypeEnum.Day;
      case EFlex.Week:
        return ISchema.ModifyAutopilotForEventMovementTypeEnum.Week;
      default:
        return ISchema.ModifyAutopilotForEventMovementTypeEnum.Day;
    }
  }

  private maybeAnimateOpacity = (numEnabledBefore: number, numEnabledAfter: number) => {
    const { canAutopilotMap } = this.props;

    // If cross above or below threshold, set isOpaque and set timer to set false
    const goodNumThreshold = Math.ceil(
      _.size(_.pickBy(canAutopilotMap, _.identity)) * GOOD_NUM_AP_THRESHOLD,
    );

    if (
      (numEnabledBefore < goodNumThreshold && numEnabledAfter >= goodNumThreshold) ||
      (numEnabledBefore >= goodNumThreshold && numEnabledAfter < goodNumThreshold)
    ) {
      this.setState({ isOpaque: true });
      this.opacityTimeout = setTimeout(() => {
        this.setState({ isOpaque: false });
      }, 1);
    }
  };

  private onAutopilotToggle = (isEnabled: boolean, externalEventId: string) => {
    const { org, relay, canAutopilotMap } = this.props;
    const { eventsApEnabled } = this.state;
    const event = eventsApEnabled[externalEventId].event;
    const eventParentRelayId = createEventParentRelayIdFromOrgRelayId(org.id, externalEventId);

    if (
      eventsApEnabled[externalEventId].enabled === isEnabled ||
      !canAutopilotMap[externalEventId]
    ) {
      return;
    }
    if (!eventParentRelayId) {
      // could not create eventParentRelayId to save event, show an error;
      return;
    }

    const newEventsApEnabled = Object.assign({}, this.state.eventsApEnabled),
      numEnabledBefore = _.filter(eventsApEnabled, (e) => e.enabled).length;
    newEventsApEnabled[externalEventId].enabled = isEnabled;
    const numEnabledAfter = _.filter(newEventsApEnabled, (e) => e.enabled).length;

    this.maybeAnimateOpacity(numEnabledBefore, numEnabledAfter);

    // Count if all are enabled, excluding events that you can't toggle.
    const allEnabled = _.every(
      newEventsApEnabled,
      (e) => e.enabled || !canAutopilotMap[e.event.eventKey.externalEventId],
    );

    this.setState({ eventsApEnabled: newEventsApEnabled, toggleAllState: allEnabled });

    const trackingEvent = isEnabled
      ? TrackingEvents.ONE_ON_ONES_AUTOPILOT.ENABLE_AUTOPILOT_FOR_ONE_ON_ONE
      : TrackingEvents.ONE_ON_ONES_AUTOPILOT.DISABLE_AUTOPILOT_FOR_ONE_ON_ONE;
    track(trackingEvent, {
      externalEventId: externalEventId,
    });

    // construct new tag and attempt to set to previous flexibility type if previously was on AP.
    const flexTag = isEnabled
      ? eventsApEnabled[externalEventId].flexType === EFlex.Week
        ? EFlex.Week
        : EFlex.Day
      : EFlex.Pin;
    const saveTags = generateEventFormSaveTags(flexTag, false, false, undefined);

    const input: ISchema.IAddAndRemoveOrgTagsMutationInput = {
      eventParentRelayId,
      orgRelayId: org.id,
      calendarId: (event.calendarIds && event.calendarIds[0]) || event.organizer?.urnValue,
      ...saveTags,
    };

    new Promise((resolve, reject) => {
      addAndRemoveOrgTags(relay.environment, input, resolve, reject);
    }).catch(_.partial(this.onRevertEventApState, externalEventId, !isEnabled).bind(this));
  };

  private onRevertEventApState(externalEventId: string, apEnabledStateToSet: boolean) {
    const t = this.context;
    const newEventsApEnabled = Object.assign({}, this.state.eventsApEnabled);
    newEventsApEnabled[externalEventId].enabled = apEnabledStateToSet;
    this.setState({ eventsApEnabled: newEventsApEnabled });
    // Present an error of some sort and log it.
    logger.error("Failed to toggle Autopilot for One on One");
    this.props.snackBarOpen(t("1-on-1:toggle-error") as string);
  }

  private onToggleAllAp(e: React.ChangeEvent<HTMLSelectElement>) {
    const t = this.context;
    const toggleState = e.target.value,
      { eventsApEnabled } = this.state;
    const { org, relay, canAutopilotMap, oneOnOnesForCalendar } = this.props;

    if (toggleState === _radioOptions.ALL) {
      // Clone eventsApEnabled to create storeable previous state
      const prevEventsApEnabled = oneOnOnesForCalendar.reduce(
        (prev: IEventApStatus, event: ISchema.IEvent) => {
          const externalEventId = event.eventKey.externalEventId;
          prev[externalEventId] = {
            enabled: eventsApEnabled[externalEventId].enabled,
            event: event,
            flexType: eventsApEnabled[externalEventId].flexType,
          };
          return prev;
        },
        {},
      );

      // Enable for all possible
      const nextEventsApEnabled = oneOnOnesForCalendar.reduce(
        (prev: IEventApStatus, event: ISchema.IEvent) => {
          const externalEventId = event.eventKey.externalEventId;
          const enabled = canAutopilotMap[externalEventId];
          prev[externalEventId] = {
            enabled,
            event: event,
            flexType: eventsApEnabled[externalEventId].flexType,
          };
          return prev;
        },
        {},
      );

      const numEnabledBefore = _.filter(prevEventsApEnabled, (e) => e.enabled).length;
      const numEnabledAfter = _.filter(nextEventsApEnabled, (e) => e.enabled).length;
      this.maybeAnimateOpacity(numEnabledBefore, numEnabledAfter);

      this.setState({
        toggleAllState: true,
        prevEventsApEnabled: prevEventsApEnabled,
        eventsApEnabled: nextEventsApEnabled,
      });

      const input: ISchema.IBatchModifyAutopilotForEventsMutationInput = {
        orgRelayId: org.id,
        events: _.compact(
          oneOnOnesForCalendar.map((e) => {
            const externalEventId = e.eventKey.externalEventId;
            // Skip events that we cannot enable, and ones that are already enabled.
            if (!canAutopilotMap[externalEventId] || prevEventsApEnabled[externalEventId].enabled) {
              return false; // _.compact filters out falsy values
            }
            return {
              externalEventId: e.eventKey.externalEventId,
              movementType: this.maybeGetPreviousMovementType(externalEventId),
            } as ISchema.IAutopilotEventToModify;
          }),
        ),
      };

      if (input.events?.length) {
        batchModifyAutopilotForEvents(
          relay.environment,
          input,
          () => {
            track(TrackingEvents.ONE_ON_ONES_AUTOPILOT.ENABLE_AUTOPILOT_FOR_ALL);
          },
          () => {
            logger.error("Failed to enable Autopilot for all One on Ones");
            this.props.snackBarOpen(t("1-on-1:mark-all-error") as string);
            this.revertToPrevApState();
          },
        );
      }
    } else {
      this.revertToPrevApState();
      track(TrackingEvents.ONE_ON_ONES_AUTOPILOT.DISABLE_AUTOPILOT_FOR_ALL);
    }
  }

  private revertToPrevApState() {
    const { eventsApEnabled, prevEventsApEnabled } = this.state;
    const { org, relay, canAutopilotMap, oneOnOnesForCalendar } = this.props;

    const numEnabledBefore = _.filter(eventsApEnabled, (e) => e.enabled).length;
    const numEnabledAfter = _.filter(prevEventsApEnabled, (e) => e.enabled).length;
    this.maybeAnimateOpacity(numEnabledBefore, numEnabledAfter);

    this.setState({ toggleAllState: false, eventsApEnabled: prevEventsApEnabled });

    const input: ISchema.IBatchModifyAutopilotForEventsMutationInput = {
      orgRelayId: org.id,
      events: _.compact(
        oneOnOnesForCalendar.map((e) => {
          const externalEventId = e.eventKey.externalEventId;
          // Filter out disabled events and events that were previous enabled.
          if (!canAutopilotMap[externalEventId] || prevEventsApEnabled[externalEventId].enabled) {
            return false; // _.compact filters out falsy values
          }
          return {
            externalEventId: e.eventKey.externalEventId,
            movementType: ISchema.ModifyAutopilotForEventMovementTypeEnum.None,
          } as ISchema.IAutopilotEventToModify;
        }),
      ),
    };

    if (input.events?.length) {
      batchModifyAutopilotForEvents(
        relay.environment,
        input,
        () => {},
        () => {
          logger.error("Failed to rever Autopilot state for One on Ones");
        },
      );
    }
  }

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-

  public render() {
    const { org, canAutopilotMap, oneOnOnesForCalendar } = this.props,
      { eventsApEnabled, toggleAllState, isOpaque } = this.state;

    const numEvents = _.size(_.pickBy(canAutopilotMap, _.identity)),
      goodNumAP = Math.ceil(numEvents * GOOD_NUM_AP_THRESHOLD);

    return (
      <div className="cw-flex cw-flex-col cw-h-full">
        <h1 className="cw-font-brand cw-font-bold cw-text-4xl cw-text-dusk-standard cw-pt-12 cw-px-12">
          One-on-ones
        </h1>
        <div
          style={isOpaque ? styles.leftSectionBeGone : styles.leftSectionFadeIn}
          key="left-side"
          className="cw-flex cw-flex-wrap cw-flex-row cw-w-full cw-gap-4 cw-pt-12 cw-px-12"
        >
          <div className="cw-min-w-[270px] cw-grow cw-max-w-[50%]">
            <RightNumberAP
              goodNumAP={goodNumAP}
              onApCount={_.filter(eventsApEnabled, (e) => e.enabled).length}
              totalCount={numEvents}
            />
            <WhatHappensWhenAp />
          </div>
          {numEvents > 0 && (
            <div className="cw-min-w-[270px] cw-grow">
              <ToggleAllOrSome
                value={toggleAllState ? _radioOptions.ALL : _radioOptions.SOME}
                onChange={this.onToggleAllAp.bind(this)}
              />
            </div>
          )}
        </div>
        <div className="cw-px-12 cw-py-4">
          <OneOnOneAutopilotColumnsView
            org={org}
            onAutopilotToggle={this.onAutopilotToggle}
            oneOnOnesForCalendar={oneOnOnesForCalendar}
            eventsApEnabled={eventsApEnabled}
            canAutopilotMap={canAutopilotMap}
          />
        </div>
      </div>
    );
  }
}

//////////////////
// STYLES
//////////////////

const OneOnOneAutopilotViewStyled = withStyles(styles)(OneOnOneAutopilotComponent);

//////////////////
// REDUX
//////////////////

function mapStateToProps(_state: IReduxState, _ownProps: IViewProps) {
  return {};
}

export const OneOnOneAutopilotView = connect(mapStateToProps, {
  snackBarOpen,
})(OneOnOneAutopilotViewStyled);
